// © Microsoft Corporation. All rights reserved.

import { CommonHeaderConfig, CommonHeaderLocalizedStrings, NavigationItem } from "./CommonHeader.config";
import { CommonHeaderProps, CommonHeaderTelemetryContext, ContextualNavItem, HamburgerMenuData, HamburgerMenuItem, SearchProps, SearchTelemetryContext } from "./CommonHeader.props";
import { IRequestContext, InspectorArgs, InspectorResponse, getAppEnvironment } from "@msnews/core";

import { SettingsProps } from "@msnews/settings";
import { AppError } from "@msnews/diagnostics";
import { CommonHeaderIconMap } from "./CommonHeader.icons";
import { CommonHeaderState } from "./CommonHeader.state";
import { FeedNavigationData } from "@msnews/feed-navigation";
import { MeControlComponentProps } from "@msnews/me-control-component";
import { SearchFormParameters } from "@msnews/search-box";
import { TelemetryObject } from "@msnews/telemetry-core";

export interface CommonHeaderConnectorArgs {
    /** Callback for handling a navigation item content rendering */
    handleRenderInfo?: (event: CustomEvent) => void;

    /** Callback to navigate to "My Feed" */
    goToMyFeed?: () => void;

    /** Callback to navigate to "Personalize" */
    goToPersonalize?: () => void;

    /** Function to update the form parameters */
    updateFormParameters?: () => Array<SearchFormParameters>;

    /** Function to send telemetry action eventprops */
    sendTelemetryActionEvent?: (element?, action?, behavior?, event?, destination?, headline?) => void;

    /** Function to map contextual nav telemetry props */
    mapContextualNavTelemetryProps: (navItem: NavigationItem) => TelemetryObject;

    /** Function to map hamburger menu telemetry props */
    mapHamburgerMenuTelemetryProps: (navItem: NavigationItem) => TelemetryObject;

    /** Function to get the form code to use for the common search box */
    getFormCode?: (formParameters: Array<SearchFormParameters>) => string;

    /** Request id */
    requestId: string;

    /** Request context */
    requestContext: IRequestContext;

    /** Handler to log app errors.  */
    appErrorCallback?: (error: AppError) => void;

    /** Callback for language selection update */
    languageUpdatedCallback?: (market: string) => void;

    /** Telemetry data for search box */
    searchTelemetryContext: SearchTelemetryContext;

    /** boolean for secondary nav */
    isContextualSecondaryNavEnabled: boolean;

    /** Common header telemetry context */
    commonHeaderTelemetryContext?: CommonHeaderTelemetryContext;
}

export const CommonHeaderInspector: any = {
    inspect: (args: InspectorArgs<CommonHeaderConfig, CommonHeaderState, CommonHeaderConnectorArgs>): InspectorResponse<CommonHeaderProps> => {

        const { config, state, connectorArgs } = args;
        const {
            hidePersonalizeButton,
            localizedStrings,
            isDelayLoadNavAndMenuEnabled,
            disableFirstRow,
            isWindowsShellExperience,
            hideSecondaryNavAsL3InContent,
            primeOverrideVerticalKey,
            showNewLogo,
            layout
        } = config;
        const { isTtvrFired, selectedNavItem, selectedNavMenuItem, selectedSecondaryNavItem, selectedSecondaryNavMenuItem } = state;
        const { isContextualSecondaryNavEnabled, handleRenderInfo, goToMyFeed, appErrorCallback } = connectorArgs;

        // this is to improve perf for common header load for ttvr.
        const isLoadingNavAndMenu = !isDelayLoadNavAndMenuEnabled  ||
            (isDelayLoadNavAndMenuEnabled && isTtvrFired);

        const logoAltText = showNewLogo ? localizedStrings && localizedStrings.newLogoAltText : localizedStrings && localizedStrings.logoAltText;

        const props: CommonHeaderProps = {
            hamburgerMenuProps: getHamburgerMenuProps(config, state, connectorArgs, isLoadingNavAndMenu),
            contextualNavProps: getContextualNavProps(config, connectorArgs, isLoadingNavAndMenu),
            contextualSecondaryNavProps: isContextualSecondaryNavEnabled ? getContextualSecondaryNavProps(config, connectorArgs, isLoadingNavAndMenu) : null,
            isContextualSecondaryNavEnabled,
            handleRenderInfo: handleRenderInfo,
            selectedNavItem,
            selectedNavMenuItem,
            selectedSecondaryNavItem,
            selectedSecondaryNavMenuItem,
            searchProps: !disableFirstRow ? getSearchProps(config, state, connectorArgs) : null,
            meControlProps: !disableFirstRow ? getMeControlProps(config, connectorArgs) : null,
            goToMyFeed: goToMyFeed,
            disableFirstRow,
            isWindowsShellExperience,
            hideSecondaryNavAsL3InContent,
            primeOverrideVerticalKey,
            hidePersonalizeButton,
            appErrorCallback: appErrorCallback,
            settingsProps: !disableFirstRow ? getSettingsProps(config, connectorArgs) : null,
            logoAltText,
            showNewLogo,
            layout
        };

        return {
            props: props,
            missingChildren: []
        };
    }
};

/**
 * Gets the hamburger menu props from config and state
 * @param config navigation config
 * @param state common header state
 * @param state common header state
 * @param connectorArgs common header state
 * @param isResolvingHamburgerMenuItems flag to decide whether to resolve hamburger menu items
 */
function getHamburgerMenuProps(
    config: CommonHeaderConfig,
    state: Partial<CommonHeaderState>,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingHamburgerMenuItems: boolean): HamburgerMenuData<HamburgerMenuItem> {
    const { navigationConfig, localizedStrings } = config;
    if (!navigationConfig.hamburgerMenuItems.length) {
        return null;
    }

    // Convert the items from the config to hamburger menu items
    const hamburgerMenuItems = !isResolvingHamburgerMenuItems ? null : navigationConfig.hamburgerMenuItems.map(item => convertNavigationItemToHamburgerMenuItem(item, connectorArgs, localizedStrings));

    return {
        menuButtonTooltip: localizedStrings && localizedStrings.menuButtonTooltip,
        closeButtonTooltip: localizedStrings && localizedStrings.closeButtonTooltip,
        navItems: hamburgerMenuItems,
        selectedNavId: state.selectedNavItem && state.selectedNavItem.id
    };
}

/**
 * Convert navigation items to menu item props
 * @param navigationItem navigation item
 */
function convertNavigationItemToHamburgerMenuItem(item: NavigationItem, connectorArgs: CommonHeaderConnectorArgs, localizedStrings: CommonHeaderLocalizedStrings): HamburgerMenuItem {
    if (!item) {
        return null;
    }

    let childItems = null;
    if (item.children && item.children.length) {
        childItems = item.children.map(child => convertNavigationItemToHamburgerMenuItem(child, connectorArgs, localizedStrings));
    }

    return {
        ...item,
        display: item.displayKey ? localizedStrings[item.displayKey] : item.display,
        glyph: CommonHeaderIconMap[item.iconName],
        childItems: childItems,
        telemetryMetadata: connectorArgs.mapHamburgerMenuTelemetryProps(item)
    };
}

/**
 * Gets the contextual nav props from config
 * @param config common header config info
 * @param connectorArgs connector arguments
 * @param isResolvingFeedsMenuItems flag to determine resolving feeds menu items
 */
function getContextualNavProps(
    config: CommonHeaderConfig,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingFeedsMenuItems: boolean
): FeedNavigationData<ContextualNavItem> {
    const { navigationConfig, localizedStrings } = config || {};

    if (!navigationConfig.contextualNavItems.length) {
        return null;
    }

    return {
        navGroups: {
            home: (config.isWindowsShellExperience ?? true) ? convertNavigationItemToFeedNavigationItem(navigationConfig.homeNavigationItem, connectorArgs, localizedStrings, config.enableHomeContextualNavIcon) : null,
            feeds: isResolvingFeedsMenuItems ?
                navigationConfig.contextualNavItems.map(item => convertNavigationItemToFeedNavigationItem(item, connectorArgs, localizedStrings))
                : []
        },
        localizedStrings: {
            overflowButtonTooltip: localizedStrings.overflowButtonTooltip,
            personalizeButtonText: localizedStrings.personalizeButtonText
        },
        selectedNavItem: null,
        selectedNavMenuItem: null,
        interestsNavData: convertNavigationItemToFeedNavigationItem(navigationConfig.interestsNavigationItem, connectorArgs, localizedStrings)
    };
}
/**
 * Gets the contextual secondary nav props from config
 * @param config common header config
 * @param connectorArgs connector arguments
 * @param isResolvingFeedsMenuItems flag to determine resolving feeds menu items
 */
function getContextualSecondaryNavProps(
    config: CommonHeaderConfig,
    connectorArgs: CommonHeaderConnectorArgs,
    isResolvingFeedsMenuItems: boolean
): FeedNavigationData<ContextualNavItem> {
    const { navigationConfig, localizedStrings } = config || {};

    if (!navigationConfig.contextualNavItems.length) {
        return null;
    }

    return {
        navGroups: {
            home: convertNavigationItemToFeedNavigationItem(navigationConfig.secondaryNavigationItem, connectorArgs, localizedStrings, config.enableHomeContextualNavIcon),
            feeds: isResolvingFeedsMenuItems ?
                navigationConfig.contextualNavItems.map(item => convertNavigationItemToFeedNavigationItem(item, connectorArgs, localizedStrings))
                : []
        },
        localizedStrings: {
            overflowButtonTooltip: localizedStrings.overflowButtonTooltip,
            personalizeButtonText: localizedStrings.personalizeButtonText
        },
        selectedNavItem: null,
        selectedNavMenuItem: null,
        interestsNavData: null
    };
}

/**
 * Convert navigation item to feed navigation item
 * @param item navigation item
 */
function convertNavigationItemToFeedNavigationItem(item: NavigationItem, connectorArgs: CommonHeaderConnectorArgs, localizedStrings: CommonHeaderLocalizedStrings, enableHomeContextualNavIcon?: boolean): ContextualNavItem {
    if (!item) {
        return null;
    }

    const { renderInfo, children = [] } = item;

    // TODO: Move path property into NavigationItem object instead of RenderInfo
    return {
        ...item,
        display: item.displayKey ? localizedStrings[item.displayKey] : item.display,
        glyph: enableHomeContextualNavIcon && CommonHeaderIconMap[item.iconName],
        path: renderInfo && renderInfo.path,
        children: children.length ? children.map(child => convertNavigationItemToFeedNavigationItem(child, connectorArgs, localizedStrings, enableHomeContextualNavIcon)) : [],
        telemetryMetadata: connectorArgs.mapContextualNavTelemetryProps(item)
    };
}

/**
 * Gets props for the search box
 * @param config common header config
 * @param state common header state
 * @param connectorArgs common header connector args
 */
function getSearchProps(config: CommonHeaderConfig, state: Partial<CommonHeaderState>, connectorArgs: CommonHeaderConnectorArgs): SearchProps {
    const { searchBoxConfig, localizedStrings } = config;
    const { getFormCode, requestId, searchTelemetryContext, sendTelemetryActionEvent, updateFormParameters } = connectorArgs;

    const formParameters = updateFormParameters && updateFormParameters();

    const searchProps: SearchProps = {
        // We set the id in the common header component
        id: undefined,
        action: searchBoxConfig.searchActionUrl,
        autoFocus: searchBoxConfig.enableSearchFormFocus,
        autoSuggest: searchBoxConfig.autoSuggest,
        "data-form-code": getFormCode && getFormCode(formParameters),
        formParameters,
        isOnImage: true,
        keyPressCaptureEnabled: searchBoxConfig.enableKeyPressCapture,
        maxLength: 250,
        placeholder: localizedStrings && localizedStrings.searchPlaceholderText,
        requestId,
        target: searchBoxConfig.target,
        title: localizedStrings && localizedStrings.searchTooltip,
        buttonLabel: localizedStrings && localizedStrings.searchButtonTooltip,
        telemetryContext: searchTelemetryContext || {},
        telemetryCallback: sendTelemetryActionEvent
    };

    if (searchProps.telemetryContext.buttonObject && searchProps.telemetryContext.buttonObject.contract) {
        searchTelemetryContext.buttonObject.contract.destinationUrl = searchProps.action;
    }

    return searchProps;
}

/**
 * Gets props for the me control
 * @param config common header config
 */
function getMeControlProps(config: CommonHeaderConfig, connectorArgs: CommonHeaderConnectorArgs): MeControlComponentProps {
    const { meControlConfig, localizedStrings } = config;
    const { signedInWidePageDisplayMode, signedInNarrowPageDisplayMode, signedOutWidePageDisplayMode, signedOutNarrowPageDisplayMode } = meControlConfig;

    return {
        partnerId: meControlConfig.partnerId,
        signInTooltipText: localizedStrings && localizedStrings.signInTooltip,
        signedInNarrowPageDisplayMode,
        signedOutNarrowPageDisplayMode,
        signedInWidePageDisplayMode,
        signedOutWidePageDisplayMode,
        requestContext: connectorArgs.requestContext
    };
}

/**
 * Gets props for the settings dialog
 * @param config common header config
 * @param connectorArgs common header connector args
 */
function getSettingsProps(config: CommonHeaderConfig, connectorArgs: CommonHeaderConnectorArgs): SettingsProps {
    const { localizedStrings } = config;
    const { languageUpdatedCallback, goToPersonalize, commonHeaderTelemetryContext } = connectorArgs;

    const marketSelectorProps = {
        id: "languageSelector",
        onValueChange: languageUpdatedCallback,
        defaultMarketId: getAppEnvironment().CurrentMarket || "en-us",
        marketList: commonHeaderTelemetryContext && commonHeaderTelemetryContext.languageOptions,
        label: localizedStrings && localizedStrings.languageDropdownLabel,
        marketSelectorMetaTag: commonHeaderTelemetryContext && commonHeaderTelemetryContext.settingsLanguageSelectorMetaTag
    };

    return {
        goToPersonalize: goToPersonalize,
        isCnDomain: getAppEnvironment().IsCnDomain,
        localizedStrings: {
            settingsDialogTitle: localizedStrings && localizedStrings.settingsDialogTitle,
            languageDropdownLabel: localizedStrings && localizedStrings.languageDropdownLabel,
            settingsDialogPersonalizeLabel: localizedStrings && localizedStrings.settingsDialogPersonalizeLabel,
            settingsDialogPersonalizeButtonText: localizedStrings && localizedStrings.settingsDialogPersonalizeButtonText,
            settingsButtonTooltip: localizedStrings && localizedStrings.settingsButtonTooltip,
            settingsCloseButtonTooltip: localizedStrings && localizedStrings.settingsCloseButtonTooltip
        },
        marketSelectorProps
    };
}
